import { useState } from "react";
import clsx from "clsx";
// React Query
import { useMutation } from "@tanstack/react-query";
// MUI
import { Avatar, Card, CardContent, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
// Third Party
import moment from "moment";

// APIs
import { createReviewResponse } from "@/DataPages/api/socialMediaReviews/mutations";

// Utils
import { or } from "@Common/helpers/sonar.tsx";

// Components
import StarRating from "@/Layouts/components/RatingSelect/StarRating";
import SimpleTextField from "@Layouts/components/InputComponents/SimpleTextField";
import ReviewResponses from "@/DataPages/components/CRM/SocialMediaReviews/components/ReviewResponses";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

// Assets
import SendButton from "@/assets/icons/ratings/sendButton.png";
import DisabledSendButton from "@/assets/icons/ratings/disabled-reply-review-icon.svg";
import LinkEnabled from "@/assets/icons/ratings/link-enabled.svg";
import LinkDisabled from "@/assets/icons/ratings/link-disabled.svg";

// Types
import { IReviewDetail } from "@DataPages/types/socialMediaReviews/model";
import { ReviewDialogSource } from "@DataPages/types/socialMediaReviews/utils";

type ReviewCardProps = Readonly<{
	hasReplyReviewPermission: boolean;
	reviewDetail: IReviewDetail;
	locationId: string;
	siteName: string;
	source: ReviewDialogSource;
}>;

export default function ReviewCard(props: ReviewCardProps) {
	const { reviewDetail, hasReplyReviewPermission, locationId, source, siteName } = props;
	const { author, review, reviewDate, needsResponse, reviewUid, attributions } = reviewDetail;
	const { body, rating } = review ?? {};

	// Local State
	const [textResponse, setTextResponse] = useState("");

	const isLinkInvited = attributions?.some((attribution) => Boolean(attribution?.reviewInvitationUid));

	/********************************************* MUTATIONS ************************************************/
	const { mutate: replyReviewMutation, isPending } = useMutation({
		mutationKey: ["createReviewResponse"],
		mutationFn: async () => createReviewResponse({ locationId, reviewUid, textResponse, siteName, source }),
		onSettled: () => {
			setTextResponse("");
		},
	});

	return (
		<Card sx={{ boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.3)", borderRadius: 1.5 }} component={"li"}>
			<CardContent style={{ paddingBottom: "8px" }}>
				<Stack spacing={1.5}>
					<Stack direction={"row"} alignItems="center" justifyContent="space-between" spacing={2}>
						<Avatar alt={author} src={""} />
						<Stack className="w-[90%]">
							<Stack direction={"row"} spacing={1} alignItems="center" justifyContent="space-between">
								<Stack direction={"row"} spacing={1} alignItems="center">
									<Typography className={"!font-montserrat !font-medium !text-sm !text-default"}>{author}</Typography>
									<Typography className={"!font-sans !font-normal !text-sm !text-[#b3b3b3]"} component={"span"}>
										{needsResponse && "(Needs Response)"}
									</Typography>
								</Stack>
								<Tooltip title={isLinkInvited ? "Link invite sent" : ""} arrow>
									<img
										src={isLinkInvited ? LinkEnabled : LinkDisabled}
										style={{ width: "20px", height: "10px" }}
										alt="link"
									/>
								</Tooltip>
							</Stack>
							<Typography className={"!font-sans !text-[14.5px] !text-default"}>
								{moment(reviewDate).utc().format(DATE_FORMAT.monthAbbrDate)}
							</Typography>
						</Stack>
					</Stack>
					<StarRating value={rating} maxStars={5} />
					<Typography className={"!font-sans !text-[14.5px] !text-default"}>{body}</Typography>
				</Stack>

				{/* Review Responses */}
				<ReviewResponses responses={reviewDetail?.responses ?? []} />

				<Divider className="!mt-[15px] !mb-[10px] !bg-[#CCCCCC] " />
				<Stack direction={"row"} alignItems={"center"} spacing={1}>
					<SimpleTextField
						outlined
						editable
						text={textResponse}
						disabled={or(!hasReplyReviewPermission, isPending)}
						placeholder={"Type your message and press Enter to send"}
						classes={clsx({
							"h-[30px]": true,
							"bg-[#F9F9F9]": !hasReplyReviewPermission,
						})}
						// Callbacks
						onChange={(value) => setTextResponse(value)}
						onActionUpdate={() => setTextResponse("")}
					/>
					<IconButton onClick={() => replyReviewMutation()} disabled={or(!hasReplyReviewPermission, isPending, !textResponse)}>
						{renderSendControl(!hasReplyReviewPermission || !textResponse, isPending)}
					</IconButton>
				</Stack>
			</CardContent>
		</Card>
	);
}

function renderSendControl(isDisabled: boolean, isPending: boolean) {
	if (isPending) {
		return <CircularProgress size="30px" />;
	}
	if (isDisabled) {
		return <img src={DisabledSendButton} style={{ width: 30 }} alt="reply-icon" />;
	}
	return <img src={SendButton} style={{ width: 30 }} alt="reply-icon" />;
}
